import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Download, FileText, Code2, Loader2, ShieldCheck } from 'lucide-react';
import { ResearchJob } from '../../types';
import { jobsApi } from '../../services/api';

interface ResearchResultsProps {
  job: ResearchJob;
}

export function ResearchResults({ job }: ResearchResultsProps) {
  const [view, setView] = useState<'rendered' | 'raw'>('rendered');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isGeneratingDocx, setIsGeneratingDocx] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleDownloadMd = () => {
    if (!job.research_output_md) return;
    const blob = new Blob([job.research_output_md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${job.title.replace(/\s+/g, '-').toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadDocx = async () => {
    setIsGeneratingDocx(true);
    try {
      const response = await jobsApi.downloadDocx(job.id);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${job.title.replace(/\s+/g, '-').toLowerCase()}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsGeneratingDocx(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!contentRef.current) return;
    setIsGeneratingPdf(true);
    // Clone the rendered memo and append the Urava footer so the client-side
    // PDF carries branding like the server-generated one does.
    const clone = contentRef.current.cloneNode(true) as HTMLDivElement;
    const footer = document.createElement('div');
    footer.style.cssText =
      'margin-top:32pt;padding-top:10pt;border-top:1px solid #2d3748;font-size:10pt;font-style:italic;color:#4a5568;';
    const date = new Date().toLocaleDateString('en-IN', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
    footer.textContent = `Generated by Urava — urava.app · ${date} · AI-generated research; verify citations against primary sources.`;
    clone.appendChild(footer);
    // html2canvas needs the node in the document to resolve styles — park it offscreen.
    clone.style.position = 'fixed';
    clone.style.left = '-10000px';
    clone.style.top = '0';
    clone.style.width = `${contentRef.current.offsetWidth}px`;
    document.body.appendChild(clone);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const filename = `${job.title.replace(/\s+/g, '-').toLowerCase()}.pdf`;
      await html2pdf()
        .set({
          margin: [0.75, 0.75, 0.75, 0.75],
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, letterRendering: true },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        } as any)
        .from(clone)
        .save();
    } finally {
      clone.remove();
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-legal-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-legal-200">
        <div className="flex space-x-1">
          <button
            onClick={() => setView('rendered')}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              view === 'rendered'
                ? 'bg-legal-100 text-legal-900'
                : 'text-legal-500 hover:text-legal-700'
            }`}
          >
            <FileText className="h-4 w-4" />
            <span>Preview</span>
          </button>
          <button
            onClick={() => setView('raw')}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              view === 'raw'
                ? 'bg-legal-100 text-legal-900'
                : 'text-legal-500 hover:text-legal-700'
            }`}
          >
            <Code2 className="h-4 w-4" />
            <span>Markdown</span>
          </button>
        </div>

        <div className="flex items-center space-x-2">
          {job.citations_total != null && job.citations_total > 0 && (
            <span
              data-tour="citations-badge"
              title={`We checked ${job.citations_verified ?? 0} of ${job.citations_total} citations against live sources. Citations we couldn't check are listed in the PDF's appendix.`}
              className={`hidden sm:flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                job.citations_verified === job.citations_total
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                  : 'border-amber-300 bg-amber-50 text-amber-700'
              }`}
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>
                We checked {job.citations_verified ?? 0} of {job.citations_total} citations against live sources
              </span>
            </span>
          )}
          <button
            onClick={handleDownloadMd}
            className="flex items-center space-x-1.5 px-3 py-1.5 border border-legal-300 rounded-md text-sm font-medium text-legal-700 hover:bg-legal-50 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Markdown</span>
          </button>
          <button
            onClick={handleDownloadDocx}
            disabled={isGeneratingDocx}
            className="flex items-center space-x-1.5 px-3 py-1.5 border border-legal-300 rounded-md text-sm font-medium text-legal-700 hover:bg-legal-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isGeneratingDocx ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span>{isGeneratingDocx ? 'Generating…' : 'Word'}</span>
          </button>
          <button
            onClick={handleDownloadPdf}
            disabled={isGeneratingPdf}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-accent text-legal-900 rounded-md text-sm font-medium hover:bg-accent-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isGeneratingPdf ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span>{isGeneratingPdf ? 'Generating…' : 'PDF'}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-h-[70vh] overflow-y-auto">
        {view === 'rendered' ? (
          <div
            ref={contentRef}
            className="prose prose-slate max-w-none
              prose-headings:font-bold prose-headings:text-legal-900
              prose-h1:text-2xl prose-h1:text-center prose-h1:mb-6
              prose-h2:text-xl prose-h2:border-b prose-h2:border-legal-200 prose-h2:pb-2 prose-h2:mt-8
              prose-h3:text-base prose-h3:font-semibold prose-h3:text-legal-800
              prose-p:text-legal-700 prose-p:leading-relaxed
              prose-a:text-accent-600 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-legal-900
              prose-blockquote:border-l-4 prose-blockquote:border-accent prose-blockquote:bg-accent-50 prose-blockquote:not-italic prose-blockquote:rounded-r
              prose-table:text-sm
              prose-thead:bg-legal-100
              prose-th:px-3 prose-th:py-2 prose-th:text-legal-800 prose-th:font-semibold prose-th:border prose-th:border-legal-300
              prose-td:px-3 prose-td:py-2 prose-td:border prose-td:border-legal-200 prose-td:text-legal-700
              prose-li:text-legal-700
              prose-hr:border-legal-200
              font-serif
            "
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {job.research_output_md || ''}
            </ReactMarkdown>
          </div>
        ) : (
          <pre className="text-sm text-legal-700 whitespace-pre-wrap font-mono bg-legal-50 p-4 rounded-lg overflow-x-auto">
            {job.research_output_md}
          </pre>
        )}
      </div>
    </div>
  );
}
